import Blanket from '@atlaskit/blanket';
import { IconButton } from '@atlaskit/button/new';
import { Label , ErrorMessage, HelperMessage } from '@atlaskit/form';
import CopyIcon from '@atlaskit/icon/core/copy';
import QuestionCircleIcon from '@atlaskit/icon/core/question-circle';
import { Box } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import Select from '@atlaskit/select';
import Spinner from '@atlaskit/spinner';
import Textfield from '@atlaskit/textfield';
import Tooltip from '@atlaskit/tooltip';
import debounce from 'lodash/debounce';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import useLocalStorage from '@/components/common/hooks/useLocalStorage';

import { useAppContext } from '../../../store';
import { CloudSite, InstallationDetails, SubscriptionDetails, InstallationStatus } from '../../common/types';
import { OnboardingLayout } from '../OnboardingLayout';

import { CloudSiteReplacementModal } from './CloudSiteReplacementModal';
import { EmailUpdateModal } from './EmailUpdateModal';




interface CloudMappingRequest {
    enterpriseId: string;
    teamId: string;
    cloudId: string;
    defaultAgentId: string;
    botAaid: string;
    apiKey: string;
    createdBy: string;
    systemAccountEmail: string;
}

interface AgentCacheEntry {
    agentId: string;
    timestamp: number;
}

interface ExistingMapping {
    teamId: string;
    enterpriseId: string;
    cloudId: string;
    apiKey?: string;
    botAaid?: string;
    botEmail?: string;
    defaultAgentId: string;
    createdBy: string;
    createdAt: number;
    updatedAt: number;
}

export const CloudMapping = () => {
    const { state } = useAppContext();
    const { userProfile, subscriptionDetails } = state;
    const history = useHistory();
    
    // Cache for storing siteId to defaultAgentId mapping (30 minutes TTL)
    const agentCache = useRef<Map<string, AgentCacheEntry>>(new Map());
    const CACHE_TTL = 30 * 60 * 1000; // 30 minutes in milliseconds
        
    // Cache helper functions
    const getCachedAgentId = (siteId: string): string | null => {
        const cached = agentCache.current.get(siteId);
        if (!cached) return null;
        
        const now = Date.now();
        if (now - cached.timestamp > CACHE_TTL) {
            // Cache expired, remove entry
            agentCache.current.delete(siteId);
            return null;
        }
        
        return cached.agentId;
    };
    
    const setCachedAgentId = (siteId: string, agentId: string): void => {
        agentCache.current.set(siteId, {
            agentId,
            timestamp: Date.now()
        });
    };
    
    const [cloudMapping, setCloudMapping] = useState<CloudMappingRequest>({
        enterpriseId: '',
        teamId: '',
        cloudId: '',
        defaultAgentId: '',
        botAaid: '',
        apiKey: 'dummy',
        createdBy: userProfile?.account_id || '',
        systemAccountEmail: ''
    });
    const [emailValidation, setEmailValidation] = useState<{
        isValid: boolean;
        isChecking: boolean;
        message: string;
        botAaid?: string;
    }>({
        isValid: false,
        isChecking: false,
        message: ''
    });
    const [isCreating, setIsCreating] = useState(false);
    const [isLoadingOperations, setIsLoadingOperations] = useState(false);
    const [siteError, setSiteError] = useState<string>('');
    const [emailError, setEmailError] = useState<string>('');
    const [apiError, setApiError] = useState<string>('');
    const [rovoError, setRovoError] = useState<string>('');
    const [isLoadingAgents, setIsLoadingAgents] = useState(false);
    const [isEmailUpdateModalOpen, setIsEmailUpdateModalOpen] = useState(false);
    const [isCloudSiteReplacementModalOpen, setIsCloudSiteReplacementModalOpen] = useState(false);
    const [installationDetails, setInstallationDetails] = useLocalStorage<InstallationDetails | null>('installation_details', null);
    const [existingMapping, setExistingMappings] = useState<ExistingMapping | null>(null);
    const [cloudSiteToOrgMap, setCloudSiteToOrgMap] = useState<Record<string, string> | null>(null);

    useEffect(() => {
        const fetchOrgs = async () => {
            try {
                const result = await fetch('/gateway/api/rovo-slack-svc/api/v1/cloudSites/list-orgs', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        aaid: userProfile?.account_id,
                        email: userProfile?.email
                    })
                });
                const orgs = await result.json();
                
                const map: Record<string, string> = {};
                orgs.forEach((org: any) => {
                    org.sites.forEach((site: any) => {
                        map[site.id] = org.id;
                    });
                });

                setCloudSiteToOrgMap(map);
            } catch (error) {
                console.error('Error fetching orgs:', error);
            }
        }
        fetchOrgs();
    }, [userProfile]);

    // Extract enterpriseId and teamId from URL parameters and fetch defaultAgentId
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const enterpriseIdParam = urlParams.get('enterpriseId');
        const teamIdParam = urlParams.get('teamId');

        // check if cloudmapping exists and initialise state accordingly
        const checkCloudMapping = async () => {
            if (teamIdParam) {
                setIsLoadingOperations(true);
                const existingMappingResponse = await checkExistingMappings(teamIdParam);
                setExistingMappings(existingMappingResponse);
                setIsLoadingOperations(false);
            }
        }

        checkCloudMapping();
        
        if (enterpriseIdParam || teamIdParam) {
            setCloudMapping(prev => ({
                ...prev,
                enterpriseId: enterpriseIdParam || prev.enterpriseId,
                teamId: teamIdParam || prev.teamId
            }));

            setInstallationDetails({
                enterpriseId: enterpriseIdParam || '',
                teamId: teamIdParam || '',
                status: InstallationStatus.INSTALLED,
                error: undefined
            });
        } else {
            if(installationDetails && installationDetails.enterpriseId && installationDetails.teamId) {

                setCloudMapping(prev => ({
                    ...prev,
                    enterpriseId: installationDetails.enterpriseId,
                    teamId: installationDetails.teamId
                }));
            }
            history.push(`/slack/onboard/sitedetails?enterpriseId=${installationDetails?.enterpriseId}&teamId=${installationDetails?.teamId}`);
        }

    }, [setCloudMapping]);

    useEffect(() => {
        if (existingMapping) {
            setCloudMapping(prev => ({
                ...prev,
                cloudId: existingMapping.cloudId,
                defaultAgentId: existingMapping.defaultAgentId,
                botAaid: existingMapping.botAaid as string,
                systemAccountEmail: existingMapping.botEmail as string
            }));

            setEmailValidation(prev => ({
                ...prev,
                isValid: true,
                isChecking: false,
                message: 'Email is valid',
                botAaid: existingMapping.botAaid as string
            }));
        }
    }, [existingMapping]);

    // API functions
    const validateEmail = async (email: string): Promise<{ isValid: boolean; botAaid?: string }> => {
        try {
            const response = await fetch(`/gateway/api/rovo-slack-svc/api/v1/validate-system-account`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    cloudId: cloudMapping.cloudId,
                    systemEmail: email,
                    product: 'rovo',
                    defaultAgentId: cloudMapping.defaultAgentId
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return {
                isValid: data.hasAccess || false,
                botAaid: data.botAaid
            };
        } catch (error) {
            console.error('Error validating email:', error);
            throw error;
        }
    };

    const checkExistingMappings = async (teamId: string): Promise<ExistingMapping | null> => {                
        const response = await fetch(`/gateway/api/rovo-slack-svc/api/v2/slack-cloud-mappings/team/${teamId}`)
        
        if (!response.ok) {
            return null;
        }

        const data = await response.json();

        return data || null;
    };

    const createCloudMapping = async (): Promise<boolean> => {
        try {
            const response = await fetch('/gateway/api/rovo-slack-svc/api/v2/slack-cloud-mappings', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(cloudMapping)
            });

            if (!response.ok) {
                if(response.status === 401 || response.status === 403){
                    throw new Error('You do not have permission to update this cloud mapping');
                } else {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
            }

            return true;
        } catch (error) {
            console.error('Error creating cloud mapping:', error);
            throw error;
        }
    };

    const updateCloudMapping = async (): Promise<boolean> => {
        try {
            const response = await fetch('/gateway/api/rovo-slack-svc/api/v2/slack-cloud-mappings', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(cloudMapping)
            });

            if (!response.ok) {
                if(response.status === 401 || response.status === 403){
                    throw new Error('You do not have permission to update this cloud mapping');
                } else {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
            }

            return true;
        } catch (error) {
            console.error('Error updating cloud mapping:', error);
            throw error;
        }
    };

    const fetchDefaultAgentId = async (cloudId: string): Promise<string> => {
        // Check cache first
        const cachedAgentId = getCachedAgentId(cloudId);
        if (cachedAgentId) {
            console.log('Using cached agent ID for site:', cloudId);
            return cachedAgentId;
        }

        try {
            const url = `/gateway/api/rovo-slack-svc/api/v1/defaultAgent/${cloudId}?aaid=${userProfile?.account_id}`
            
            const response = await fetch(url, {
                method: 'GET'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const agents = await response.json();
            
            if (!agents) {
                throw new Error('No default agent found');
            }

            const agentId = agents.agentId;
            
            // Cache the result
            setCachedAgentId(cloudId, agentId);
            console.log('Cached agent ID for site:', cloudId);
            
            return agentId;
        } catch (error) {
            console.error('Error fetching default agent ID:', error);
            throw error;
        }
    };

    // Generate site options based on subscription details
    const getSiteOptions = () => {

        if (!subscriptionDetails || (subscriptionDetails as SubscriptionDetails)?.length === 0) {
            return [];
        };
        
        return subscriptionDetails.map((site: CloudSite) => ({
            label: site.hostname,
            value: site.cloudSiteId
        }));
    };

    // Email format validation helper
    const isValidEmailFormat = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Perform email validation API call
    const performEmailValidation = async (email: string) => {
        if (!email) {
            setEmailValidation({
                isValid: false,
                isChecking: false,
                message: ''
            });
            return;
        }

        // Only validate if email format is valid
        if (!isValidEmailFormat(email)) {
            setEmailValidation({
                isValid: false,
                isChecking: false,
                message: 'Please enter a valid email format'
            });
            return;
        }

        setEmailValidation(prev => ({
            ...prev,
            isChecking: true,
            message: 'Validating email...'
        }));

        setIsLoadingOperations(true);

        try {
            const validationResponse = await validateEmail(email);
            setEmailValidation({
                isValid: validationResponse.isValid,
                isChecking: false,
                message: validationResponse.isValid ? 'Email is valid' : 'Email does not have access to this site',
                ...(validationResponse.botAaid && { botAaid: validationResponse.botAaid })
            });
            
            if (validationResponse.isValid && validationResponse.botAaid) {
                setCloudMapping(prev => ({
                    ...prev,
                    botAaid: validationResponse.botAaid || ''
                }));
            }
        } catch (error) {
            setEmailValidation({
                isValid: false,
                isChecking: false,
                message: 'Error validating email'
            });
        } finally {
            setIsLoadingOperations(false);
        }
    };

    // Debounced email validation using lodash
    const debouncedEmailValidation = useCallback(
        debounce(performEmailValidation, 800),
        [cloudMapping.cloudId, cloudMapping.defaultAgentId]
    );

    // Cleanup debounced function on unmount
    useEffect(() => {
        return () => {
            debouncedEmailValidation.cancel();
        };
    }, [debouncedEmailValidation]);

    // Handle site selection change
    const handleSiteChange = async (option: any) => {
        const cloudId = option?.value || '';
        setCloudMapping(prev => ({
            ...prev,
            cloudId
        }));
        setSiteError(''); // Clear site error when user makes a selection
        setApiError(''); // Clear API error when user makes changes
        setRovoError(''); // Clear Rovo error when user makes changes
        setIsLoadingAgents(false); // Reset loading state

        // Fetch default agent ID when a site is selected
        if (cloudId) {
            setIsLoadingAgents(true);
            setIsLoadingOperations(true);
            try {
                const agentId = await fetchDefaultAgentId(cloudId);
                console.log('Default agent ID:', agentId);
                setCloudMapping(prev => ({
                    ...prev,
                    defaultAgentId: agentId
                }));
            } catch (error) {
                console.error('Error fetching default agent ID:', error);
                setRovoError('This cloud site does not have Rovo enabled.');
            } finally {
                setIsLoadingAgents(false);
                setIsLoadingOperations(false);
            }
        }
    };

    // Handle email change with debounced validation
    const handleEmailChange = (value: string) => {
        setCloudMapping(prev => ({
            ...prev,
            systemAccountEmail: value
        }));
        setEmailError(''); // Clear email error when user types
        setApiError(''); // Clear API error when user makes changes
        
        // Use debounced validation
        debouncedEmailValidation(value);
    };

    // Modal handlers
    const handleEmailUpdateConfirm = async (): Promise<void> => {
        setIsEmailUpdateModalOpen(false);
        try {
            const success = await updateCloudMapping();
            if (success) {
                history.push(`/slack/onboard/complete?cloudId=${  cloudMapping.cloudId  }&teamId=${  cloudMapping.teamId  }&enterpriseId=${  cloudMapping.enterpriseId}`);
            } else {
                setApiError('Failed to update cloud mapping. Please try again.');
            }
        } catch (error) {
            console.error('Error updating cloud mapping:', error);
            const errorMessage = error instanceof Error ? error.message : 'An error occurred while updating cloud mapping. Please try again.';
            setApiError(errorMessage);
        } finally {
            setIsCreating(false);
        }
    };

    const handleEmailUpdateCancel = () => {
        setIsEmailUpdateModalOpen(false);
        setIsCreating(false);
    };

    const handleCloudSiteReplacementConfirm = async (): Promise<void> => {
        setIsCloudSiteReplacementModalOpen(false);
        try {            
            // update mapping
            const success = await updateCloudMapping();
            if (success) {
                history.push(`/slack/onboard/complete?cloudId=${  cloudMapping.cloudId  }&teamId=${  cloudMapping.teamId  }&enterpriseId=${  cloudMapping.enterpriseId}`);
            } else {
                setApiError('Failed to create cloud mapping. Please try again.');
            }
        } catch (error) {
            console.error('Error creating cloud mapping:', error);
            const errorMessage = error instanceof Error ? error.message : 'An error occurred while creating cloud mapping. Please try again.';
            setApiError(errorMessage);
        } finally {
            setIsCreating(false);
        }
    };

    const handleCloudSiteReplacementCancel = () => {
        setIsCloudSiteReplacementModalOpen(false);
        setIsCreating(false);
    };

    // Handle next button click
    const handleNext = async () => {

        setSiteError(''); // Clear any previous errors
        setEmailError('');
        setApiError('');
        setRovoError(''); // Clear Rovo error
        
        if (!cloudMapping.cloudId) {
            setSiteError('Please select a site to continue');
            return false;
        }

        if (!emailValidation.isValid) {
            setEmailError('Please enter a valid email address');
            return false;
        }

        setIsCreating(true);
        
        try {
            // Check for existing mappings
            const existingMappingResponse = await checkExistingMappings(cloudMapping.teamId);
            
            if (existingMappingResponse) {
                // If user doesnt update the anything on cloudMapping, then we can just return true
                if (existingMappingResponse.cloudId === cloudMapping.cloudId 
                    && existingMappingResponse.botEmail === cloudMapping.systemAccountEmail 
                    && existingMappingResponse.botAaid === cloudMapping.botAaid
                    && existingMappingResponse.defaultAgentId === cloudMapping.defaultAgentId
                    && existingMappingResponse.teamId === cloudMapping.teamId
                    && (existingMappingResponse.enterpriseId === cloudMapping.enterpriseId)
                ) {
                    return {
                        shouldProceed: true,
                        searchParams: {
                            enterpriseId: cloudMapping.enterpriseId,
                            teamId: cloudMapping.teamId,
                            cloudId: cloudMapping.cloudId,
                        }
                    }
                }
                
                if (existingMappingResponse && existingMappingResponse.cloudId === cloudMapping.cloudId) {
                    // Same cloudId - show email update modal
                    setIsEmailUpdateModalOpen(true);
                    return false;
                } else {
                    // Different cloudId - show replacement modal
                    setIsCloudSiteReplacementModalOpen(true);
                    return false;
                }
            } else {
                // No existing mapping - create new one
                const success = await createCloudMapping();
                if (success) {
                    console.log('Cloud mapping created successfully');
                    return {
                        shouldProceed: true,
                        searchParams: {
                            enterpriseId: cloudMapping.enterpriseId,
                            teamId: cloudMapping.teamId,
                            cloudId: cloudMapping.cloudId,
                        }
                    }
                } else {
                    setApiError('Failed to create cloud mapping. Please try again.');
                    return false;
                }
            }
        } catch (error) {
            console.error('Error creating cloud mapping:', error);
            const errorMessage = error instanceof Error ? error.message : 'An error occurred while creating cloud mapping. Please try again.';
            setApiError(errorMessage);
            return false;
        } finally {
            setIsCreating(false);
        }
    };


    const getAdminHubURL = () => {
        if(cloudSiteToOrgMap && cloudMapping.cloudId){
            if(process.env['NODE_ENV'] === 'production'){
                return `https://admin.atlassian.com/o/${cloudSiteToOrgMap[cloudMapping.cloudId]}/members`;
            } else {
                return `https://admin.stg.atlassian.com/o/${cloudSiteToOrgMap[cloudMapping.cloudId]}/members`;
            }
        }
        return '';
    }

    const hasErrors = () : boolean => {
        if(siteError != '' || emailError != '' || apiError != '' || rovoError != '') {
            return true;
        }
        if(!cloudMapping.cloudId) {
            return true;
        }
        if(!cloudMapping.systemAccountEmail) {
            return true;
        }
        if(!emailValidation.isValid) {
            return true;
        }
        return false;
    };

    return (
        <OnboardingLayout currentStep={2} onNext={handleNext} onNextLabel="Finish" isNextDisabled={hasErrors}>
            <Box>
                <h3>Add site details</h3>

                <Box>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', marginTop: '16px' }}>
                        <SectionMessage appearance="information">
                            <p>You need to be an org admin to complete onboarding.</p>
                            <p>If you are not an org admin, please share the link with your org admin to complete onboarding.
                                <IconButton onClick={() => {
                                    navigator.clipboard.writeText(window.location.href);
                                }}
                                icon={CopyIcon} label="copy link"
                                />    
                            </p>                            
                        </SectionMessage>
                    </Box>
                </Box>

                {existingMapping && (
                    <Box>
                        <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', marginTop: '16px' }}>
                            <SectionMessage appearance="success">
                                <p>Existing mapping found for team {existingMapping.teamId}. You can either update or finish onboarding.</p>
                            </SectionMessage>
                        </Box>
                    </Box>
                )}

                <Box style={{ width: '400px', marginBottom: '16px' }}>
                    <Label htmlFor="site-name">Atlassian site</Label>
                    <Select
                        name='site-name'
                        id='site-name'
                        options={getSiteOptions()}
                        placeholder="Select a site"
                        value={cloudMapping.cloudId ? (() => {
                            const selectedSite = subscriptionDetails?.find(site => site.cloudSiteId === cloudMapping.cloudId);
                            return selectedSite ? { label: selectedSite.hostname, value: selectedSite.cloudSiteId } : null;
                        })() : null}
                        onChange={handleSiteChange}
                        defaultValue={existingMapping?.cloudId ? (() => {
                            const selectedSite = subscriptionDetails?.find(site => site.cloudSiteId === existingMapping?.cloudId);
                            console.log('selectedSite', selectedSite);
                            return selectedSite ? { label: selectedSite.hostname, value: selectedSite.cloudSiteId } : null;
                        })() : null}
                    />
                    {!siteError && (
                        <HelperMessage>
                            Choose the site for Rovo to use in Slack.
                        </HelperMessage>
                    )}
                    {siteError && (
                        <ErrorMessage>
                            {siteError}
                        </ErrorMessage>
                    )}
                    {isLoadingAgents && (
                        <Box style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginTop: '8px'
                        }}>
                            <Spinner size="small" />
                            <span style={{ fontSize: '12px', color: '#42526E' }}>
                                Checking Rovo availability...
                            </span>
                        </Box>
                    )}
                    {rovoError && (
                        <ErrorMessage>
                            {rovoError}
                        </ErrorMessage>
                    )}
                </Box>
                
                <Box style={{ width: '400px', marginBottom: '16px' }}>
                    <Tooltip position="right" content="This is what Rovo will use to determine its permission and access for any queries in public channels or groups. Make sure this account has atleast read access across your Atlassian apps. Service accounts are not supported.">
                            {(tooltipProps) => (
                                <div {...tooltipProps}>
                                    <Label  htmlFor="system-account-email">Bot account</Label>
                                    <span style={{ marginLeft: '4px' }} />
                                    <QuestionCircleIcon label="what is a bot account" />
                                </div>
                            )}
                    </Tooltip>
                    <Textfield 
                        name='system-account-email' 
                        id='system-account-email'
                        value={cloudMapping.systemAccountEmail}
                        placeholder="Enter email"
                        onChange={(e) => handleEmailChange((e.target as HTMLInputElement).value)}
                        isInvalid={!!(emailValidation.message && !emailValidation.isValid) || !!emailError}
                        isRequired
                        isDisabled={cloudMapping.cloudId ? false : true}
                        defaultValue={existingMapping?.botEmail || ''}
                    />
                    {!emailError && !emailValidation.message && (
                        <HelperMessage>
                            Enter the email of the bot account. If you dont have one, <a href={getAdminHubURL()} target="_blank" rel="noreferrer">Create</a>.
                        </HelperMessage>
                    )}

                    {emailValidation.message && !emailValidation.isValid && (
                        <ErrorMessage>
                            {`${emailValidation.message} - (Note: Service accounts are not supported)`}
                        </ErrorMessage>
                    )}
                    
                    {emailError && (
                        <ErrorMessage>
                            {`${emailError} - (Note: Service accounts are not supported)`}
                        </ErrorMessage>
                    )}
                </Box>
                
                {apiError && (
                    <Box style={{ marginBottom: '16px' }}>
                        <ErrorMessage>
                            {apiError}
                        </ErrorMessage>
                    </Box>
                )}
                
                {isCreating && (
                    <Blanket isTinted>
                        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <Spinner size="large" />
                        </Box>
                    </Blanket>
                )}

                {isLoadingOperations && (
                    <Blanket isTinted>
                        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <Spinner size="large" />
                        </Box>
                    </Blanket>
                )}

                {/* Modal Components */}
                <EmailUpdateModal
                    isOpen={isEmailUpdateModalOpen}
                    onConfirm={handleEmailUpdateConfirm}
                    onCancel={handleEmailUpdateCancel}
                />
                
                <CloudSiteReplacementModal
                    isOpen={isCloudSiteReplacementModalOpen}
                    onConfirm={handleCloudSiteReplacementConfirm}
                    onCancel={handleCloudSiteReplacementCancel}
                />
            </Box>  
        </OnboardingLayout>
    );
};